import { z } from 'zod'
import {
  fabricVariantSchema,
  manufacturerSchema,
  newFabricProductSchema,
  newPillowProductSchema,
  newPremadeProductSchema,
  newTableProductSchema,
  newTowelProductSchema,
  pillowVariantSchema,
  placeSchema,
  premadeVariantSchema,
  tableVariantSchema,
  towelVariantSchema,
} from './schemas'

export enum Unit {
  METER = 'METER',
  CENTIMETER = 'CENTIMETER',
  PIECE = 'PIECE',
}

export enum Category {
  FABRIC = 'FABRIC',
  PREMADE = 'PREMADE',
  PILLOW = 'PILLOW',
  TOWEL = 'TOWEL',
  TABLE = 'TABLE',
}

export enum PillowType {
  PREMADE = 'PREMADE',
  FABRIC = 'FABRIC',
}

export enum TableType {
  TABLECLOTH = 'TABLECLOTH',
  TABLE_RUNNER = 'TABLE_RUNNER',
  NAPKIN = 'NAPKIN',
}

export type Place = z.infer<typeof placeSchema>

export type Product = {
  id: string
  symbol: string
  alternateSymbol?: string | null
  image?: string | null
  isAtSale: boolean
  manufacturer?: Manufacturer
  availableSizes?: Array<string> | null
}

export type ProductSymbols = {
  id: string
  symbol: string
  alternateSymbol?: string | null
}

export type Manufacturer = {
  id: string
  name: string
  email: string
}

export type EditProduct = Omit<Product, 'manufacturer'> & {
  manufacturerId: string
}

export type VariantBase = {
  productId: string
  id: string
  size: string
  place: Place & { id: string }
  stock: number
  unit: Unit
  category: Category
  comments?: string | null
}

export interface FabricVariant extends VariantBase {
  category: Category.FABRIC
  feature?: string | null
}

export interface PremadeVariant extends VariantBase {
  category: Category.PREMADE
  finish: Array<string>
}

export interface TowelVariant extends VariantBase {
  category: Category.TOWEL
  feature?: string | null
}

export interface PillowVariant extends VariantBase {
  category: Category.PILLOW
  pillowType: PillowType
  feature?: string | null
}

export interface TableVariant extends VariantBase {
  category: Category.TABLE
  tableType: TableType
  feature?: string | null
}

export type Variant = FabricVariant | PremadeVariant | PillowVariant | TowelVariant | TableVariant

export type ProductWithVariants = Product & {
  variants: {
    [Category.FABRIC]: Array<FabricVariant>
    [Category.PREMADE]: Array<PremadeVariant>
    [Category.PILLOW]: Array<PillowVariant>
    [Category.TOWEL]: Array<TowelVariant>
    [Category.TABLE]: Array<TableVariant>
  }
}

type NewVariant<T> = Omit<T, 'id' | 'productId' | 'place'> & {
  place: Place
}

export type NewVariantWithProduct<T> = NewVariant<T> & {
  symbol: string
  alternateSymbol?: string | null
  manufacturerId: string
  isAtSale: boolean
  image?: string | null
  availableSizes?: Array<string> | null
}

export type NewProductWithoutVariant = {
  symbol: string
  alternateSymbol?: string | null
  manufacturerId: string
  isAtSale: boolean
  image?: string | null
  availableSizes?: Array<string> | null
}

export type NewVariantWithExistingProduct<T> = NewVariant<T> & {
  productId: string
}

export type NewProduct<T> = NewVariantWithProduct<T> | NewVariantWithExistingProduct<T>

export interface EditVariant extends Omit<VariantBase, 'productId' | 'place'> {
  finish?: Array<string>
  pillowType?: string | null
  feature?: string | null
  place: Place
}

export type FabricVariantFields = z.infer<typeof fabricVariantSchema>
export type PremadeVariantFields = z.infer<typeof premadeVariantSchema>
export type PillowVariantFields = z.infer<typeof pillowVariantSchema>
export type TowelVariantFields = z.infer<typeof towelVariantSchema>
export type TableVariantFields = z.infer<typeof tableVariantSchema>

export type NewFabricProduct = z.infer<typeof newFabricProductSchema>
export type NewPremadeProduct = z.infer<typeof newPremadeProductSchema>
export type NewPillowProduct = z.infer<typeof newPillowProductSchema>
export type NewTowelProduct = z.infer<typeof newTowelProductSchema>
export type NewTableProduct = z.infer<typeof newTableProductSchema>

export type NewManufacturer = z.infer<typeof manufacturerSchema>
export type UpdateManufacturer = NewManufacturer
